import { useEffect } from 'react';

interface StructuredDataProps {
  type?: 'organization' | 'website' | 'service' | 'breadcrumb' | 'webApplication';
  data?: any;
}

const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://interview-r.com';

const defaultOrganizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'InterviewR',
  url: baseUrl,
  logo: `${baseUrl}/logo192.png`,
  description: 'Expert interview preparation platform connecting job seekers with industry professionals for mock interviews.',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Tbilisi',
    addressCountry: 'GE'
  },
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'info@interview-r.com',
    contactType: 'Customer Service'
  },
  founder: {
    '@type': 'Person',
    name: 'Nikoloz Turkishvili',
    alternateName: 'ნიკოლოზ თურქიშვილი'
  },
  sameAs: []
};

const defaultWebsiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'InterviewR',
  url: baseUrl,
  description: 'Expert interview preparation platform connecting job seekers with industry professionals for mock interviews.',
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${baseUrl}/interviewers?search={search_term_string}`
    },
    'query-input': 'required name=search_term_string'
  }
};

const defaultServiceSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  serviceType: 'Mock Interview Service',
  provider: {
    '@type': 'Organization',
    name: 'InterviewR'
  },
  areaServed: {
    '@type': 'Country',
    name: 'Georgia'
  },
  description: 'Professional mock interview services connecting job seekers with industry experts for interview practice and skill improvement.'
};

const defaultWebApplicationSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebApplication',
  name: 'InterviewR',
  url: baseUrl,
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'USD',
    description: 'Free AI mock interview demo; paid expert sessions available',
  },
};

export function StructuredData({ type = 'organization', data }: StructuredDataProps) {
  useEffect(() => {
    const scriptId = `structured-data-${type}`;
    const existingScript = document.getElementById(scriptId);
    if (existingScript) {
      existingScript.remove();
    }

    let schema;
    switch (type) {
      case 'organization':
        schema = data || defaultOrganizationSchema;
        break;
      case 'website':
        schema = data || defaultWebsiteSchema;
        break;
      case 'service':
        schema = data || defaultServiceSchema;
        break;
      case 'webApplication':
        schema = data || defaultWebApplicationSchema;
        break;
      case 'breadcrumb':
        schema = data;
        break;
      default:
        schema = defaultOrganizationSchema;
    }

    if (schema) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
    }

    return () => {
      const script = document.getElementById(scriptId);
      if (script) {
        script.remove();
      }
    };
  }, [type, data]);

  return null;
}

